import axios from 'axios';

// Dynamic API URL resolution: In local development, always connect directly to the backend port 5006
const getApiBaseUrl = () => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl && envUrl.startsWith('http')) {
    return envUrl;
  }
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5006/api';
    }
  }
  return envUrl || '/api';
};

const API_URL = getApiBaseUrl();

const __publicGetCache = new Map();
const __publicGetInflight = new Map();

const publicGet = (url, options = {}, ttlMs = 60_000) => {
  const now = Date.now();
  const cached = __publicGetCache.get(url);
  if (cached && cached.expiresAt > now) {
    return Promise.resolve(cached.response);
  }

  const inflight = __publicGetInflight.get(url);
  if (inflight) return inflight;

  const req = api.get(url, options)
    .then((res) => {
      // Only cache if valid non-empty data is returned
      const data = res.data?.data;
      const hasValidData = Array.isArray(data) ? data.length > 0 : Boolean(data);
      if (hasValidData) {
        __publicGetCache.set(url, { response: res, expiresAt: now + ttlMs });
      }
      return res;
    })
    .finally(() => {
      __publicGetInflight.delete(url);
    });

  __publicGetInflight.set(url, req);
  return req;
};

const api = axios.create({
  baseURL: API_URL,
  timeout: 8000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if the current URL is /admin/setup or /admin/login to prevent redirection loops
    const currentPath = window.location.pathname;
    const isAuthPage = currentPath === '/admin/setup' || currentPath === '/admin/login';
    
    if (error.response?.status === 401 && !isAuthPage) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Export instance
export { api };

// Auth API
export const authAPI = {
  setupAdmin: (data) => api.post('/auth/setup-admin', data),
  login: (credentials) => api.post('/auth/login', credentials),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/password', data),
  register: (data) => api.post('/auth/register', data),
  checkSetup: () => api.get('/auth/check-setup'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.put(`/auth/reset-password/${token}`, { password }),
};

// Articles API
export const articlesAPI = {
  getAll: (params) => api.get('/articles', { params }),
  getBySlug: (slug) => api.get(`/articles/${slug}`),
  getById: (id) => api.get(`/articles/${id}`),
  create: (data) => api.post('/articles', data),
  update: (id, data) => api.put(`/articles/${id}`, data),
  delete: (id) => api.delete(`/articles/${id}`),
  getBreaking: () => publicGet('/articles/breaking', undefined, 30_000),
  getTrending: (limit = 10) => publicGet(`/articles/trending?limit=${limit}`, undefined, 30_000),
  getFeatured: () => publicGet('/articles/featured', undefined, 30_000),
  getRelated: (id) => api.get(`/articles/${id}/related`),
  bulkDelete: (ids) => api.post('/articles/bulk-delete', { ids }),
  bulkUpdateStatus: (ids, status) => api.post('/articles/bulk-status', { ids, status }),
};

// Categories API
export const categoriesAPI = {
  getAll: () => publicGet('/categories', undefined, 5 * 60_000),
  getAllAdmin: () => api.get(`/categories?includeInactive=true&_t=${Date.now()}`),
  getBySlug: (slug) => api.get(`/categories/${slug}?_t=${Date.now()}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  toggle: (id) => api.patch(`/categories/${id}/toggle`),
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  toggleStatus: (id) => api.put(`/users/${id}/toggle-status`),
  bulkDelete: (ids) => api.post('/users/bulk-delete', { ids }),
  bulkUpdateRole: (ids, role) => api.post('/users/bulk-role', { ids, role }),
};

// Media API
export const mediaAPI = {
  getAll: (params) => api.get('/media', { params }),
  upload: (formData) => api.post('/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/media/${id}`),
  bulkDelete: (ids) => api.post('/media/bulk-delete', { ids }),
  move: (ids, folder) => api.post('/media/move', { ids, folder }),
  createFolder: (name) => api.post('/media/folders', { name }),
};

// Subscribers API
export const subscriberAPI = {
  subscribe: (data) => api.post('/subscribers', data),
  getAll: (params) => api.get('/subscribers', { params }),
  delete: (id) => api.delete(`/subscribers/${id}`),
  bulkDelete: (ids) => api.post('/subscribers/bulk-delete', { ids }),
  getStats: () => api.get('/subscribers/stats'),
};

export const subscribersAPI = subscriberAPI;

// Analytics API
export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getArticles: (period) => api.get(`/analytics/articles?period=${period}`),
  getActivityLogs: (params) => api.get('/analytics/activity', { params }),
  reorderCategories: (orders) => api.post('/analytics/categories/reorder', { orders }),
};

// Settings API
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  update: (data) => api.post('/settings', data),
  getPublic: () => publicGet('/settings/public', undefined, 5 * 60_000),
};

export default api;
